import type { Edge, Node, XYPosition } from '@xyflow/react';
import type { CSSProperties } from 'react';

/* Extends a type that can be used in a flow with additional information that the flow's consumer
 * can determine but is not directly on the type.  */
export type NodeData<T extends Record<string, unknown>, NT extends string = string> = T & {
  readonly id: string;
  readonly type: NT;
  handles: string[];
};

export type Adjacency<U extends Record<string, unknown>, ET extends string = string> = {
  childID: string;
  type: ET;
  data?: U;
};
export type AdjacencyList<U extends Record<string, unknown>, ET extends string = string> = Record<
  string,
  Record<string, Adjacency<U, ET>>
>;

export type LabelComponentProps<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> = {
  id?: string;
  sourceHandleId?: string;
  /* If null, the target is an input node */
  targetHandleId?: string | null;
  data?: U;
  source?: T2;
  target?: T2;
};
export type LabelComponent<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> = (edge: LabelComponentProps<T, U, NT, T2>) => JSX.Element | null;

export type LabelType<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> =
  | ({
      labelComponent: LabelComponent<T, U, NT, T2>;
      colour?: CSSProperties['strokeColor'];
      selectedColour?: CSSProperties['strokeColor'];
      opacity?: CSSProperties['opacity'];
      width?: CSSProperties['strokeWidth'];
      dashed?: CSSProperties['strokeDasharray'];
    } & Pick<Edge, 'markerStart' | 'markerEnd' | 'reconnectable'>)
  | LabelComponent<T, U>;

type DummyNodeData = { dummy: 'dummy' };

type NodeForest<
  T extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T> = NodeData<T>,
> = {
  node: Node<T2 | DummyNodeData>;
  children: NodeForest<T, NT, T2>[];
  bounds: Bound[];
};

type DataForest<
  T extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T> = NodeData<T>,
> = {
  node?: T2;
  children: DataForest<T, NT, T2>[];
};

type Bound = { upper: number; lower: number } | 'missing';

export function arrangeNodes<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
>(
  nodes: T2[],
  edges: AdjacencyList<U, ET>,
  block_width: number,
  block_height: number,
  vertical: boolean,
): Node<T>[] {
  const nodeById = nodes.reduce(
    (a, x) => {
      a[x.id] = x;
      return a;
    },
    {} as Record<string, T2>,
  );

  // Contains nodes that have been converted into TForest<T> but have not had their
  // parents converted
  const forest: Record<string, DataForest<T2>> = {};

  const buildForest: (node: T2) => DataForest<T2> = (node) => {
    const existing = forest[node.id];
    if (existing) {
      delete forest[node.id];
      return existing;
    }

    const children: DataForest<T2>[] = [];
    Object.values(edges[node.id] ?? {}).forEach(({ childID }) => {
      const childNode = nodeById[childID];
      if (!childNode) return;
      const child = buildForest(childNode);
      forest[childID] = child;
      children.push(child);
    });

    // Sort children in order of the node's handles
    const indexOfChildren = Object.entries(edges[node.id] ?? {})
      .map(
        ([handle, { childID }]) =>
          [node.handles.indexOf(handle) ?? -1, childID] satisfies [number, string],
      )
      .reduce(
        (a, [i, child]) => {
          a[child] = Math.max(a[child] ?? -1, i);
          return a;
        },
        {} as Record<string, number>,
      );
    children.sort(
      (a, b) =>
        ((b.node && indexOfChildren[b.node.id]) ?? -1) -
        ((a.node && indexOfChildren[a.node.id]) ?? -1),
    );

    const out = { node, children };
    forest[node.id] = out;
    return out;
  };

  nodes.forEach((node) => {
    buildForest(node);
  });

  return flattenAbsolute(
    positionRelative({ children: Object.values(forest) }, block_width, block_height, vertical),
    { x: 0, y: 0 },
  );
}

// Flattens tree, making positions absolute
function flattenAbsolute<
  T extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T> = NodeData<T>,
>({ node, children }: NodeForest<T, NT, T2>, { x, y }: XYPosition): Node<T>[] {
  node.position.x += x;
  node.position.y += y;
  const childNodes = children.flatMap((child) =>
    flattenAbsolute(child, { x: node.position.x, y: node.position.y }),
  );
  // Filter out dummy nodes with {} data
  return 'id' in node.data ? [{ ...node, data: node.data }, ...childNodes] : childNodes;
}

export const convertNode: <
  T extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
>(
  node: T2,
  pos?: XYPosition,
) => Node<T2> = (node, pos) => ({
  id: node.id,
  type: node.type,
  data: node,
  position: pos || { x: 0, y: 0 },
  origin: [0.5, 0.5],
});

// Converts a tree of T into a tree of Node, positioning children relative to their parents
function positionRelative<
  T extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T> = NodeData<T>,
>(
  node: DataForest<T, NT, T2>,
  block_width: number,
  block_height: number,
  vertical: boolean,
): NodeForest<T, NT, T2> {
  const out = {
    node:
      node.node !== undefined
        ? convertNode(node.node)
        : {
            id: 'DUMMY_NODE_ID',
            type: 'DUMMY_NODE_TYPE',
            data: { dummy: 'dummy' } as DummyNodeData,
            position: { x: 0, y: 0 },
          },
    children: [],
    bounds: [
      {
        upper: (vertical ? block_width : block_height) / 2,
        lower: -(vertical ? block_width : block_height) / 2,
      },
    ],
  };

  // Base case
  if (!node.children) {
    return out;
  }

  // Recurse: stacking sub trees on stop of eachother
  const { childTrees, bounds } = node.children.reduceRight(
    ({ childTrees, bounds }, child) => {
      const childTree = positionRelative(child, block_width, block_height, vertical);
      const { ab: newBounds, maxShift } = merge(childTree.bounds, bounds);
      childTree.node.position.x += vertical ? maxShift : 0;
      childTree.node.position.y += vertical ? 0 : maxShift;
      return {
        childTrees: childTrees.concat(childTree),
        bounds: newBounds,
      };
    },
    { childTrees: [], bounds: [] } as { childTrees: NodeForest<T, NT, T2>[]; bounds: Bound[] },
  );

  // Re-center the child trees around the node
  const firstBound = bounds[0] || 'missing';
  const center =
    firstBound === 'missing' ? 0 : firstBound.lower + (firstBound.upper - firstBound.lower) / 2;
  const boundsCentered = bounds.map((b) =>
    b === 'missing' ? b : { upper: b.upper - center, lower: b.lower - center },
  );
  for (const childTree of childTrees) {
    childTree.node.position.x += vertical ? -center : block_width;
    childTree.node.position.y += vertical ? block_height : -center;
  }

  return {
    ...out,
    bounds: [...out.bounds, ...boundsCentered],
    children: childTrees,
  };
}

// Drops a on top of b also returns vector to add to a's node y pos
// Deep copies
// Doesn't actually need to be extracted since it's only used once
function merge(a: Bound[], b: Bound[]): { ab: Bound[]; maxShift: number } {
  // Special case for first stack
  if (!b.length) return { ab: a, maxShift: 0 };

  const n = Math.max(a.length, b.length);

  let maxShift = -Number.MAX_VALUE;
  for (let i = 0; i < n; i++) {
    const x = a[i] || 'missing';
    const y = b[i] || 'missing';
    if (x !== 'missing' && y !== 'missing') maxShift = Math.max(maxShift, y.upper - x.lower);
  }

  const ab: Bound[] = new Array(n).fill('missing');
  for (let i = 0; i < n; i++) {
    const x = a[i] || 'missing';
    const y = b[i] || 'missing';
    if (x === 'missing') {
      ab[i] = y === 'missing' ? y : { upper: y.upper, lower: y.lower }; // Copy
      continue;
    }
    const z = { upper: x.upper + maxShift, lower: x.lower + maxShift };
    if (y !== 'missing') {
      z.lower = y.lower;
    }
    ab[i] = z;
  }

  return { ab, maxShift };
}

export const getEdgeId = (parent: string, handle: string, childID: string) =>
  `${parent}/${handle}/${childID}`;

export function extractParentChildIDs(
  edgeID: string,
): { parent: string; handle: string; child: string } | null {
  const [parent, handle, child] = edgeID.split('/', 3);
  return parent !== undefined && handle !== undefined && child !== undefined
    ? { parent, handle, child }
    : null;
}

export function buildEdges<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
>(edges: AdjacencyList<U, ET>, edgeTypes: Record<ET, LabelType<T, U, NT, T2>>): Edge<U, ET>[] {
  return (Object.entries(edges) as [string, AdjacencyList<U, ET>[string]][]).flatMap(
    ([parent, mapping]) =>
      (Object.entries(mapping) as [string, Adjacency<U, ET>][]).map(
        ([handle, { childID, type, data }]) =>
          ({
            id: getEdgeId(parent, handle, childID),
            source: parent,
            target: childID,
            sourceHandle: handle,
            type,
            data,
            markerStart:
              typeof edgeTypes[type] === 'object' ? edgeTypes[type].markerStart : undefined,
            markerEnd: typeof edgeTypes[type] === 'object' ? edgeTypes[type].markerEnd : undefined,
            reconnectable:
              typeof edgeTypes[type] === 'object' ? edgeTypes[type].reconnectable : undefined,
          }) satisfies Edge<U, ET>,
      ),
  );
}
